// Deterministic seeded PRNG (mulberry32).
// State lives on GameState.rngSeed (immutable) + GameState.rngCursor (advances).
// Given (seed, cursor) the next draw is fully determined — same actions, same outcomes.

const STEP = 0x6d2b79f5;

function step(state: number): { value: number; next: number } {
  let t = (state + STEP) | 0;
  const next = t;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, next };
}

/**
 * Take a single random float in [0, 1) from (seed, cursor).
 * Returns the value plus the advanced cursor — the caller stores cursor back in state.
 */
export function nextFloat(seed: number, cursor: number): { value: number; nextCursor: number } {
  // Combine seed + cursor into a deterministic 32-bit state, then step.
  const combined = (seed ^ Math.imul(cursor, 0x9e3779b9)) | 0;
  const { value, next } = step(combined);
  return { value, nextCursor: (cursor + 1) | 0 };
}

/** Integer in [min, max] inclusive. */
export function nextInt(
  seed: number,
  cursor: number,
  min: number,
  max: number,
): { value: number; nextCursor: number } {
  const { value, nextCursor } = nextFloat(seed, cursor);
  return { value: Math.floor(value * (max - min + 1)) + min, nextCursor };
}

/** Weighted choice — items must have non-negative weights summing > 0. */
export function nextWeighted<T>(
  seed: number,
  cursor: number,
  items: ReadonlyArray<{ item: T; weight: number }>,
): { value: T; nextCursor: number } {
  if (items.length === 0) throw new Error('nextWeighted: empty items');
  const total = items.reduce((sum, x) => sum + Math.max(0, x.weight), 0);
  if (total <= 0) throw new Error('nextWeighted: weights sum to zero');
  const { value: r, nextCursor } = nextFloat(seed, cursor);
  const target = r * total;
  let acc = 0;
  for (const x of items) {
    acc += Math.max(0, x.weight);
    if (target < acc) return { value: x.item, nextCursor };
  }
  // Floating slack — return last.
  return { value: items[items.length - 1]!.item, nextCursor };
}

void STEP;
